import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

/**
 * Sticky breadcrumb tailored for Access V2 views.
 * Defaults to the User Management root crumb and supports optional trailing crumbs.
 */
const AccessBreadcrumb = ({ crumbs = [], includeRoot = true, className }) => {
  const navigate = useNavigate();
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const { userGroupName, userGroupCount } = useSelector((state) => ({
    userGroupName: state.current_user_group?.name,
    userGroupCount: state.current_user_group?.memberCount,
  }));

  const userGroup = useSelector((state) => state.current_user_group);
  console.log("userGroup", userGroup);
  const rootCrumb = {
    label: "User Management",
    to: "/leader/audit/access-v2",
  };

  const trail = includeRoot ? [rootCrumb, ...crumbs] : [...crumbs];
  const lastIndex = trail.length - 1;

  const handleNavigate = (crumb) => {
    if (!crumb || (crumb.disabled && !crumb.onClick)) {
      return;
    }

    if (typeof crumb.onClick === "function") {
      crumb.onClick();
      return;
    }

    if (crumb.to) {
      navigate(crumb.to);
    }
  };

  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        rootMargin: "0px",
        threshold: 0,
      }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      <Wrapper className={className} role="navigation" aria-label="Access breadcrumb">
        <BreadcrumbBackdrop isSticky={isSticky} />
        <Inner>
          <Trail>
            {trail.map((crumb, index) => {
              const key = `${crumb?.label || "crumb"}-${index}`;
              const isActive = index === lastIndex || crumb?.active;
              const isClickable =
                (crumb?.to || crumb?.onClick) &&
                index !== lastIndex &&
                !crumb?.disabled;

              return (
                <React.Fragment key={key}>
                  <BreadcrumbItem
                    onClick={isClickable ? () => handleNavigate(crumb) : undefined}
                    clickable={isClickable}
                    active={isActive && !isClickable}
                    aria-current={isActive && !isClickable ? "page" : undefined}
                  >
                    {crumb?.label}
                  </BreadcrumbItem>
                  {index < lastIndex && <Separator>/</Separator>}
                </React.Fragment>
              );
            })}
          </Trail>
          {userGroupName && (
            <GroupMeta title="Your group">
              <span>
                {userGroupName}
                {userGroupCount != null && ` (${userGroupCount})`}
              </span>
              <InfoWrapper tabIndex="0" aria-label="Group roll-up info">
                <InfoIcon aria-hidden="true">i</InfoIcon>
                <Tooltip role="tooltip">
                  This is the total number of respondents included in your group’s roll-up.
                </Tooltip>
              </InfoWrapper>
            </GroupMeta>
          )}
        </Inner>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.nav`
  position: sticky;
  top: 0;
  z-index: 280;
  margin-bottom: 24px;
  background: transparent;
`;

const BreadcrumbBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 100vw;
  height: 100%;
  transform: ${(props) =>
    props.isSticky ? "translate(-50%, 0%)" : "translate(-50%, -100%)"};
  opacity: ${(props) => (props.isSticky ? 1 : 0)};
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(248, 250, 255, 0.92) 100%
  );
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: ${(props) =>
    props.isSticky ? "0 20px 40px rgba(15, 23, 42, 0.14)" : "none"};
  transition: transform 0.24s ease, opacity 0.24s ease, box-shadow 0.24s ease,
    background 0.24s ease;
  pointer-events: none;
  z-index: 0;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 24px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const BreadcrumbItem = styled.span`
  color: ${(props) => (props.active ? "#111827" : "#6B7280")};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) =>
      props.clickable ? "#374151" : props.active ? "#111827" : "#6B7280"};
    text-decoration: ${(props) => (props.clickable ? "underline" : "none")};
    text-underline-offset: 3px;
  }
`;

const Separator = styled.span`
  user-select: none;
  color: #d1d5db;
`;

const StickySentinel = styled.div`
  position: relative;
  width: 100%;
  height: 1px;
  margin-top: -1px;
  opacity: 0;
  pointer-events: none;
`;

const Trail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const GroupMeta = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const InfoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  color: #4b5563;
  background: #fff;
  font-size: 12px;
  cursor: default;
  outline: none;

  &:hover > div,
  &:focus > div {
    opacity: 1;
    visibility: visible;
    transform: translate(0, -4px);
  }
`;

const InfoIcon = styled.span`
  line-height: 1;
  font-weight: 700;
`;

const Tooltip = styled.div`
  position: absolute;
  top: -2px;
  right: 0;
  left: auto;
  transform: translate(0, 4px);
  background: #111827;
  color: #f9fafb;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: normal;
  text-align: left;
  max-width: 360px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.08s ease, transform 0.08s ease;
  z-index: 2;
  pointer-events: none;
`;

export default AccessBreadcrumb;
